import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
  Collapse, IconButton, TextField, MenuItem, TablePagination, Alert, Button, CircularProgress,
} from '@mui/material';
import {
  SmartToy as AiIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Person as UserIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import aiAssistantService from '../../services/ai/aiAssistant.service';
import ApiService from '../../services/api.service';
import { getBrandColor, getBrandedTableHeadSx } from '../../utils/colorUtils';
import { isTcAdmin } from '../../utils/roleUtils';

const PAGE_SIZE = 20;
const errorText = (err) => err?.response?.data?.error || err?.response?.data?.message || 'Could not load conversations';
const formatTokens = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}K` : `${n}`);

const AiConversations = ({ currentUser }) => {
  const brandColor = getBrandColor();
  const headSx = getBrandedTableHeadSx();
  const canReadContent = isTcAdmin(currentUser?.role);

  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ content: [], total_elements: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState(null);

  useEffect(() => {
    if (!currentUser?.typingCenterId) return;
    ApiService.getTypingCenterUsers(currentUser.typingCenterId)
      .then(res => setUsers(res?.data?.data || res?.data || []))
      .catch(() => setUsers([]));
  }, [currentUser]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await aiAssistantService.getSessions({ userId, from, to, page, size: PAGE_SIZE }));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  }, [userId, from, to, page]);

  useEffect(() => {
    load();
  }, [load]);

  const changeFilter = (setter) => (e) => {
    setter(e.target.value);
    setPage(0);
    setExpanded(null);
  };

  const handleExpand = async (sessionId) => {
    if (expanded === sessionId) {
      setExpanded(null);
      return;
    }
    setExpanded(sessionId);
    setDetail(null);
    setDetailError(null);
    if (!canReadContent) return;
    try {
      setDetail(await aiAssistantService.getSessionDetail(sessionId));
    } catch (err) {
      setDetailError(errorText(err));
    }
  };

  const sessions = data?.content || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <AiIcon sx={{ color: brandColor }} />
        <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>AI Conversations</Typography>
        <Chip label={`${data?.total_elements || 0} sessions`} size="small" sx={{ fontSize: '0.7rem', height: 22 }} />
      </Box>

      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField select size="small" label="Team member" value={userId} onChange={changeFilter(setUserId)}
          sx={{ minWidth: 220 }} InputProps={{ sx: { fontSize: '0.8rem' } }}>
          <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Everyone</MenuItem>
          {users.map(u => (
            <MenuItem key={u.id} value={u.id} sx={{ fontSize: '0.8rem' }}>{u.fullName || u.full_name || u.name || u.username}</MenuItem>
          ))}
        </TextField>
        <TextField type="date" size="small" label="From" value={from} onChange={changeFilter(setFrom)}
          InputLabelProps={{ shrink: true }} InputProps={{ sx: { fontSize: '0.8rem' } }} />
        <TextField type="date" size="small" label="To" value={to} onChange={changeFilter(setTo)}
          InputLabelProps={{ shrink: true }} InputProps={{ sx: { fontSize: '0.8rem' } }} />
        {(userId || from || to) && (
          <Button size="small" sx={{ textTransform: 'none' }}
            onClick={() => { setUserId(''); setFrom(''); setTo(''); setPage(0); setExpanded(null); }}>
            Clear filters
          </Button>
        )}
        {loading && <CircularProgress size={18} />}
      </Paper>

      {!canReadContent && (
        <Alert severity="info" icon={<LockIcon fontSize="small" />} sx={{ mb: 2, fontSize: '0.8rem' }}>
          You can see who used the AI Assistant and when. Only the typing center admin can read session content.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button size="small" onClick={load}>Retry</Button>}>{error}</Alert>
      )}

      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={headSx}>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, width: 40 }} />
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>User</TableCell>
                <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Messages</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Tools used</TableCell>
                <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Tokens</TableCell>
                <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Last message</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map(conv => (
                <React.Fragment key={conv.session_id}>
                  <TableRow hover onClick={() => handleExpand(conv.session_id)} sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <IconButton size="small">
                        {expanded === conv.session_id ? <CollapseIcon sx={{ fontSize: 18 }} /> : <ExpandIcon sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <UserIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography sx={{ fontSize: '0.8rem' }}>{conv.user_name || 'Unknown user'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{conv.exchange_count}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(conv.tools_used || []).slice(0, 3).map(t => (
                          <Chip key={t} label={t.replace(/_/g, ' ')} size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: 'action.selected' }} />
                        ))}
                        {(conv.tools_used || []).length > 3 && (
                          <Chip label={`+${conv.tools_used.length - 3}`} size="small" sx={{ fontSize: '0.6rem', height: 18 }} />
                        )}
                        {(conv.tools_used || []).length === 0 && (
                          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>—</Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{formatTokens(conv.total_tokens)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      {new Date(conv.last_message_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={6} sx={{ p: 0, border: expanded === conv.session_id ? undefined : 'none' }}>
                      <Collapse in={expanded === conv.session_id} unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                          {!canReadContent && (
                            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', fontStyle: 'italic' }}>
                              Only the typing center admin can read session content.
                            </Typography>
                          )}
                          {canReadContent && detailError && <Alert severity="error" sx={{ fontSize: '0.8rem' }}>{detailError}</Alert>}
                          {canReadContent && !detail && !detailError && <CircularProgress size={18} />}
                          {canReadContent && detail?.exchanges?.map(ex => (
                            <Box key={ex.exchange_id} sx={{ mb: 2 }}>
                              <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', mb: 0.5 }}>
                                {new Date(ex.timestamp).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                <UserIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.3 }} />
                                <Paper sx={{ px: 1.5, py: 1, bgcolor: brandColor, color: '#fff', borderRadius: 1.5, maxWidth: '70%' }}>
                                  <Typography sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>{ex.user_message}</Typography>
                                </Paper>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1, ml: 3 }}>
                                <AiIcon sx={{ fontSize: 16, color: brandColor, mt: 0.3 }} />
                                <Paper sx={{ px: 1.5, py: 1, borderRadius: 1.5, maxWidth: '85%', border: '1px solid', borderColor: 'divider' }}>
                                  {ex.error_message ? (
                                    <Typography sx={{ fontSize: '0.78rem', color: 'error.main' }}>Error: {ex.error_message}</Typography>
                                  ) : (
                                    <Typography component="div" sx={{ fontSize: '0.78rem', '& table': { fontSize: '0.72rem' }, '& p': { fontSize: '0.78rem', mb: 0.5 } }}>
                                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{ex.ai_response || ''}</ReactMarkdown>
                                    </Typography>
                                  )}
                                  {(ex.tools_used || []).length > 0 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                                      {ex.tools_used.map(t => (
                                        <Chip key={t} label={t.replace(/_/g, ' ')} size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: 'action.selected' }} />
                                      ))}
                                    </Box>
                                  )}
                                </Paper>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
              {!loading && sessions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>No conversations found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={data?.total_elements || 0}
          page={page}
          onPageChange={(e, p) => { setPage(p); setExpanded(null); }}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
        />
      </Paper>
    </Box>
  );
};

export default AiConversations;
